import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar, Users, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";

export default function VolunteerSignup() {
  const { ptaId } = useParams();
  const [ptaInfo, setPtaInfo] = useState<{ id: string; name: string; logo_url: string | null } | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role_task: ""
  });
  
  // Math captcha state
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  
  const generateMathQuestion = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaAnswer("");
  };
  
  useEffect(() => {
    generateMathQuestion();
  }, []);

  useEffect(() => {
    if (ptaId) {
      fetchPtaAndEvents();
    }
  }, [ptaId]);

  const fetchPtaAndEvents = async () => {
    // Fetch PTA info using short code (first 8 chars of UUID)
    const { data: ptaData } = await supabase
      .rpc('get_pta_by_short_code', { short_code: ptaId });

    if (ptaData && ptaData.length > 0) {
      setPtaInfo(ptaData[0]);
      
      // Fetch upcoming events for this PTA
      const { data: eventsData } = await supabase
        .from('events')
        .select('id, name, event_date, time, location')
        .eq('pta_id', ptaData[0].id)
        .eq('status', 'active')
        .eq('is_archived', false)
        .gte('event_date', new Date().toISOString().split('T')[0])
        .order('event_date', { ascending: true });

      setEvents(eventsData || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent || !ptaInfo) return;
    
    // Validate captcha
    if (parseInt(captchaAnswer) !== mathQuestion.answer) {
      toast.error("Incorrect answer. Please try again.");
      generateMathQuestion();
      return;
    }

    setSubmitting(true);

    const { error } = await supabase
      .from('event_helpers')
      .insert({
        event_id: selectedEvent,
        pta_id: ptaInfo.id,
        volunteer_name: formData.name,
        volunteer_email: formData.email,
        volunteer_phone: formData.phone || null,
        role_task: formData.role_task || null
      });

    if (error) {
      console.error(error);
      toast.error("Failed to sign up. Please try again.");
      generateMathQuestion();
    } else {
      setSubmitted(true);
      toast.success("Thank you for volunteering!");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!ptaInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-semibold mb-2">PTA Not Found</h2>
            <p className="text-muted-foreground">This volunteer sign-up link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">
              You have successfully signed up to volunteer. The PTA team will be in touch soon with more details.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* PTA Header */}
        <div className="text-center pt-8">
          {ptaInfo.logo_url && (
            <img 
              src={ptaInfo.logo_url} 
              alt={ptaInfo.name}
              className="h-16 w-auto mx-auto mb-4"
            />
          )}
          <h1 className="text-2xl font-bold">{ptaInfo.name}</h1>
          <p className="text-muted-foreground">Volunteer Sign-Up</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Sign Up to Help
            </CardTitle>
            <CardDescription>
              We'd love your help at our upcoming events! Fill out the form below to volunteer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="event">Select Event *</Label>
                <Select value={selectedEvent} onValueChange={setSelectedEvent} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an event..." />
                  </SelectTrigger>
                  <SelectContent>
                    {events.length === 0 ? (
                      <SelectItem value="none" disabled>No upcoming events</SelectItem>
                    ) : (
                      events.map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{event.name}</span>
                            <span className="text-muted-foreground text-xs">
                              - {format(new Date(event.event_date), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Your Name *</Label>
                <Input 
                  id="name" 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (optional)</Label>
                <Input 
                  id="phone" 
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Your phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Preferred Role/Task (optional)</Label>
                <Input 
                  id="role" 
                  value={formData.role_task}
                  onChange={(e) => setFormData({ ...formData, role_task: e.target.value })}
                  placeholder="e.g., BBQ, Face Painting, Ticket Sales"
                />
              </div>

              {/* Math Captcha */}
              <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
                <Label htmlFor="captcha">Security Question *</Label>
                <p className="text-sm text-muted-foreground">
                  What is {mathQuestion.num1} + {mathQuestion.num2}?
                </p>
                <Input 
                  id="captcha" 
                  type="number"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Enter your answer"
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting || !selectedEvent || events.length === 0 || !captchaAnswer}
              >
                {submitting ? "Signing up..." : "Sign Up to Volunteer"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Powered by PTA Hub
        </p>
      </div>
    </div>
  );
}
